(() => {
  window.conguitosNovedadesPairs?.destroy();

  const sectionSelector = '[data-novedades-editorial][data-novedades-keep-pairs="true"]';
  const gridSelector = '.novedades-editorial-restored__grid';
  const productSelector = '.novedades-editorial-restored__product';
  const bannerSelector = '.novedades-editorial-restored__banner';
  const repeatSelector = '[data-novedades-repeat-product], .novedades-editorial-restored__product--repeat';
  const records = new Map();
  const events = new AbortController();
  let frame = 0;
  let stopped = false;

  function schedule() {
    if (!frame && !stopped) frame = requestAnimationFrame(refresh);
  }

  function clear(record) {
    record.grid.removeAttribute('data-novedades-pairs-ready');
    record.placed.forEach((element) => {
      element.style.removeProperty('--novedades-pair-column');
      element.style.removeProperty('--novedades-pair-row');
    });
    record.placed.clear();
  }

  function productKey(element) {
    const link = element.querySelector('.product-card__title a[href]');
    if (!link) return null;
    try {
      return new URL(link.getAttribute('href'), document.baseURI).pathname.replace(/\/$/, '');
    } catch {
      return null;
    }
  }

  function tracks(value) {
    const values = value.trim().split(/\s+/);
    return values.every((item) => /^\d+(\.\d+)?px$/.test(item)) ? values.map(parseFloat) : null;
  }

  function offsets(sizes, gap) {
    let position = 0;
    return sizes.map((size) => {
      const start = position;
      position += size + gap;
      return start;
    });
  }

  function nearest(values, value) {
    let best = 0;
    for (let i = 1; i < values.length; i++) {
      if (Math.abs(values[i] - value) < Math.abs(values[best] - value)) best = i;
    }
    return Math.abs(values[best] - value) <= 2 ? best : -1;
  }

  function gridSpan(start, end, trackCount) {
    const span = /^span\s+(\d+)$/.exec(start) || /^span\s+(\d+)$/.exec(end);
    if (span) return Number(span[1]);
    const isLine = (value) => /^-?[1-9]\d*$/.test(value);
    if (isLine(start) && isLine(end)) {
      const lineIndex = (value) => Number(value) > 0 ? Number(value) - 1 : trackCount + 1 + Number(value);
      return lineIndex(end) - lineIndex(start);
    }
    return (start === 'auto' || isLine(start)) && (end === 'auto' || isLine(end)) ? 1 : 0;
  }

  function layout(record) {
    clear(record);
    const grid = record.grid;
    if (!grid.clientWidth) return;
    const visible = Array.from(grid.children).filter((element) => getComputedStyle(element).display !== 'none');
    if (visible.some((element) => !element.matches(`${productSelector}, ${bannerSelector}`))) return;

    const products = visible.filter((element) => element.matches(productSelector));
    const units = [];
    let hasPairs = false;
    for (let i = 0; i < products.length; i++) {
      const first = products[i];
      const second = products[i + 1];
      const key = productKey(first);
      if (second && key && key === productKey(second) && first.matches(repeatSelector) !== second.matches(repeatSelector)) {
        units.push([first, second]);
        hasPairs = true;
        i++;
      } else {
        units.push([first]);
      }
    }
    if (!hasPairs) return;

    const css = getComputedStyle(grid);
    const columns = tracks(css.gridTemplateColumns);
    const rows = tracks(css.gridTemplateRows);
    if (css.display !== 'grid' || !columns || !rows || columns.length < 2) return;
    const columnStarts = offsets(columns, parseFloat(css.columnGap) || 0);
    const rowStarts = offsets(rows, parseFloat(css.rowGap) || 0);
    const rect = grid.getBoundingClientRect();
    const rtl = css.direction === 'rtl';
    const left = rect.left + parseFloat(css.borderLeftWidth) + parseFloat(css.paddingLeft);
    const right = rect.right - parseFloat(css.borderRightWidth) - parseFloat(css.paddingRight);
    const top = rect.top + parseFloat(css.borderTopWidth) + parseFloat(css.paddingTop);
    const occupied = new Set();
    const placements = [];
    const width = columns.length;
    const mark = (row, column, rowSpan, columnSpan) => {
      for (let r = row; r < row + rowSpan; r++) {
        for (let c = column; c < column + columnSpan; c++) occupied.add(r * width + c);
      }
    };

    // Reserve the banners' natural grid cells before placing any product pairs.
    for (const banner of visible.filter((element) => element.matches(bannerSelector))) {
      const box = banner.getBoundingClientRect();
      const bannerCss = getComputedStyle(banner);
      const column = nearest(columnStarts, rtl ? right - box.right : box.left - left);
      const row = nearest(rowStarts, box.top - top);
      // The visible image may be shorter than its reserved grid area.
      const columnSpan = gridSpan(bannerCss.gridColumnStart, bannerCss.gridColumnEnd, columns.length);
      const rowSpan = gridSpan(bannerCss.gridRowStart, bannerCss.gridRowEnd, rows.length);
      if (column < 0 || row < 0 || columnSpan < 1 || rowSpan < 1 || column + columnSpan > width) return;
      mark(row, column, rowSpan, columnSpan);
      placements.push({ element: banner, row, column, rowSpan, columnSpan });
    }

    let cursor = 0;
    for (const unit of units) {
      // A single card can fill an earlier hole; pairs keep their forward order.
      let position = unit.length === 1 ? 0 : cursor;
      while (true) {
        const column = position % width;
        if (column + unit.length <= width && unit.every((_, i) => !occupied.has(position + i))) break;
        position++;
      }
      unit.forEach((element, i) => {
        const cell = position + i;
        const row = Math.floor(cell / width);
        const column = cell % width;
        mark(row, column, 1, 1);
        placements.push({ element, row, column, rowSpan: 1, columnSpan: 1 });
      });
      cursor = Math.max(cursor, position + unit.length);
    }

    placements.forEach(({ element, row, column, rowSpan, columnSpan }) => {
      element.style.setProperty('--novedades-pair-column', `${column + 1} / span ${columnSpan}`);
      element.style.setProperty('--novedades-pair-row', `${row + 1} / span ${rowSpan}`);
      record.placed.add(element);
    });
    grid.setAttribute('data-novedades-pairs-ready', '');
  }

  const resize = new ResizeObserver((entries) => {
    entries.forEach(({ target }) => {
      const record = records.get(target);
      if (record && record.width !== target.clientWidth) schedule();
    });
  });

  function refresh() {
    frame = 0;
    if (stopped) return;
    const grids = new Set(document.querySelectorAll(`${sectionSelector} ${gridSelector}`));
    for (const [grid, record] of records) {
      if (!grids.has(grid)) {
        clear(record);
        resize.unobserve(grid);
        records.delete(grid);
      }
    }
    grids.forEach((grid) => {
      if (!records.has(grid)) {
        records.set(grid, { grid, width: -1, placed: new Set() });
        resize.observe(grid);
      }
      const record = records.get(grid);
      layout(record);
      record.width = grid.clientWidth;
    });
  }

  const mutations = new MutationObserver((changes) => {
    if (changes.some((change) => {
      if (change.type === 'attributes') return change.target.matches('[data-novedades-editorial]');
      if (change.target.matches?.(gridSelector)) return true;
      return [...change.addedNodes, ...change.removedNodes].some((node) =>
        node.nodeType === 1 && (node.matches(gridSelector) || node.querySelector(gridSelector))
      );
    })) schedule();
  });

  function start() {
    mutations.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-novedades-layout', 'data-novedades-keep-pairs']
    });
    schedule();
  }

  window.conguitosNovedadesPairs = {
    refresh: schedule,
    destroy() {
      stopped = true;
      cancelAnimationFrame(frame);
      mutations.disconnect();
      resize.disconnect();
      events.abort();
      records.forEach(clear);
      records.clear();
    }
  };
  window.addEventListener('resize', schedule, { signal: events.signal });
  window.addEventListener('load', schedule, { signal: events.signal });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true, signal: events.signal });
  } else {
    start();
  }
})();
